import { useQuery } from '@apollo/client';

import { LaneType } from 'common-types';
import { FeatureFlag } from 'constants-flags';
import useFlag from 'lane-shared/hooks/useFlag';
import { getSubmissionDetails } from 'lane-shared/graphql/visitorManagement';
import { convertToUUID } from 'uuid-encoding';

import { useVisitorPassServiceData } from './useVisitorPassServiceData';
import { deriveSubmissionDetails } from '../helpers/deriveSubmissionDetails';
import { SubmissionStatus } from '../types/SubmissionDetails';

type Result = {
  status: SubmissionStatus | undefined;
  isCancellable: boolean | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

/**
 * Resolves a visitor-management submission's coarse-grained status and
 * cancellability for a given interaction, sourcing it from either the new
 * lean query or the legacy aggregate query depending on
 * FeatureFlag.VisitorManagementM2Refactor.
 *
 * Both underlying queries are called unconditionally on every render -
 * Apollo's `skip` option determines which one actually fetches. The
 * non-selected query's hook still runs (Rules of Hooks requires the same
 * hooks in the same order every render), it just never hits the network.
 */
export function useSubmissionDetails({
  interactionId,
  channelId,
  skip = false,
}: {
  interactionId?: LaneType.UUID;
  channelId?: LaneType.UUID | null;
  skip?: boolean;
}): Result {
  const isM2RefactorEnabled = useFlag(
    FeatureFlag.VisitorManagementM2Refactor,
    false
  );
  const convertedId = interactionId && convertToUUID(interactionId);
  const convertedChannelId = channelId && convertToUUID(channelId);

  const newQuery = useQuery<{
    submissionDetails: { status: SubmissionStatus; isCancellable: boolean };
  }>(getSubmissionDetails, {
    variables: { submissionId: convertedId, channelId: convertedChannelId },
    skip: skip || !convertedId || !convertedChannelId || !isM2RefactorEnabled,
  });

  const legacyQuery = useVisitorPassServiceData({
    interactionId,
    skip: skip || isM2RefactorEnabled,
  });

  if (isM2RefactorEnabled) {
    return {
      status: newQuery.data?.submissionDetails.status,
      isCancellable: newQuery.data?.submissionDetails.isCancellable,
      loading: newQuery.loading,
      error: newQuery.error,
      refetch: newQuery.refetch,
    };
  }

  const legacyDetails = deriveSubmissionDetails(
    legacyQuery.data?.visitorPassesBySubmissionId?.items
  );

  return {
    status: legacyDetails.status,
    isCancellable: legacyDetails.isCancellable,
    loading: legacyQuery.loading,
    error: legacyQuery.error,
    refetch: legacyQuery.refetch,
  };
}
